// ─────────────────────────────────────────────────────────────────────────
// CuentasChips.js — cuentas conectadas como fila de chips en la franja superior.
//
// useCuentas: estado y conexión guiada (Codex, Claude residente). "Conectar" deja la
// orden en el servidor; el latido de la cuadrilla la cumple y publica el enlace y el
// código. Mientras haya un servicio esperando aprobación se consulta cada 5 s (tope
// 15 min). Se llama desde Tablero para que el estado y el polling vivan en el padre y
// no se corten al abrir o cerrar la hoja de detalle.
//
// CuentasChips: chip por servicio (punto de estado + nombre corto). Al tocarlo se abre
// una hoja con el detalle, el botón Conectar, el enlace y el código.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback, useRef } from "react";
import backend from "../../backend";
import { C, boton, copiarTexto, Hoja } from "./componentes";

const COLOR_ESTADO = { conectado: C.verde, desconectado: C.rojo, esperando_aprobacion: C.ambar };
const TEXTO_ESTADO = {
  conectado: "Conectado",
  desconectado: "Desconectado",
  esperando_aprobacion: "Esperando aprobación",
  error: "Con error",
  desconocido: "Sin verificar",
};
// Nombre corto para el chip; el nombre completo viene del servidor y se usa en la hoja.
const NOMBRE_CORTO = {
  codex: "Codex",
  claude_residente: "Claude residente",
  github_columna: "GitHub",
  google_frigg: "Google Frigg",
  telegram_bots: "Telegram",
  simpleapi_sii: "SII",
};
// Fuente del código: la O y el 0 deben distinguirse (un código con letra O se escribió como cero y falló).
const FUENTE_CODIGO = 'ui-monospace, "JetBrains Mono", Consolas, monospace';

const haceMin = f => {
  if (!f) return null;
  const m = Math.max(0, Math.round((Date.now() - new Date(f).getTime()) / 60000));
  return m < 1 ? "verificado hace menos de 1 min" : `verificado hace ${m} min`;
};

export const useCuentas = (habilitado = true) => {
  const [cuentas, setCuentas] = useState(null);
  const [aviso, setAviso] = useState("");
  const [copiado, setCopiado] = useState(false);
  const [conectadoOk, setConectadoOk] = useState(""); // servicio recién conectado
  const previo = useRef({});
  const inicioEspera = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const res = await backend.get("tablero/cuentas");
      setCuentas(res.data.cuentas);
    } catch (err) {
      setAviso(err.response?.data?.error || "No se pudieron cargar las cuentas");
    }
  }, []);
  useEffect(() => { if (habilitado) cargar(); }, [cargar, habilitado]);

  const hayEspera = (cuentas || []).some(c => c.estado === "esperando_aprobacion");
  useEffect(() => {
    if (!hayEspera) { inicioEspera.current = null; return undefined; }
    if (!inicioEspera.current) inicioEspera.current = Date.now();
    const t = setInterval(() => {
      if (Date.now() - inicioEspera.current > 15 * 60000) { clearInterval(t); return; }
      cargar();
    }, 5000);
    return () => clearInterval(t);
  }, [hayEspera, cargar]);

  // Al pasar de "esperando aprobación" a "conectado" se avisa y el recuadro del código se cierra solo.
  useEffect(() => {
    (cuentas || []).forEach(c => {
      if (previo.current[c.servicio] === "esperando_aprobacion" && c.estado === "conectado") setConectadoOk(c.servicio);
      previo.current[c.servicio] = c.estado;
    });
  }, [cuentas]);

  const conectar = async c => {
    try {
      setAviso("");
      setConectadoOk("");
      inicioEspera.current = null;
      await backend.post(`tablero/cuentas/${c.servicio}/conectar`);
      await cargar();
    } catch (err) {
      setAviso(err.response?.data?.error || "No se pudo iniciar la conexión");
    }
  };
  const copiar = async codigo => {
    const ok = await copiarTexto(codigo);
    setCopiado(ok);
    setTimeout(() => setCopiado(false), 2500);
  };

  return { cuentas, aviso, setAviso, copiado, conectadoOk, conectar, copiar };
};

/** Detalle de una cuenta (contenido de la hoja). Sin estado propio: todo llega del padre. */
const DetalleCuenta = ({ c, copiado, conectadoOk, conectar, copiar, onCerrar }) => {
  const color = COLOR_ESTADO[c.estado] || C.suave;
  const esperando = c.estado === "esperando_aprobacion";
  const verif = haceMin(c.verificadoAt);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span aria-hidden style={{ width: 12, height: 12, flex: "0 0 12px", borderRadius: 6, background: color }} />
        <div style={{ flex: 1, fontSize: 17, fontWeight: 600, lineHeight: 1.3 }}>{c.nombre}</div>
        {c.conectable && (
          <button style={boton(C.azul)} disabled={esperando} onClick={() => conectar(c)}>
            {esperando ? "En curso…" : "Conectar"}
          </button>
        )}
      </div>
      <div style={{ fontSize: 14, color: C.suave, marginTop: 6 }}>
        <span style={{ color }}>{TEXTO_ESTADO[c.estado] || c.estado}</span>
        {c.detalle ? ` · ${c.detalle}` : ""}
        {verif ? ` · ${verif}` : ""}
      </div>

      {conectadoOk === c.servicio && c.estado === "conectado" && (
        <div style={{ marginTop: 10, color: C.verde, fontWeight: 600 }}>Conectado</div>
      )}

      {esperando && !c.url && (
        <div style={{ marginTop: 10, fontSize: 14, color: C.ambar }}>Preparando el enlace de conexión…</div>
      )}

      {esperando && c.url && c.codigo && (
        <div style={{ marginTop: 12, background: C.campo, border: `1px solid ${C.ambar}`, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>1. Abre este enlace</div>
          <button
            style={{ ...boton(C.azul, true), marginTop: 8, width: "100%" }}
            onClick={() => window.open(c.url, "_blank", "noopener,noreferrer")}
          >
            Abrir enlace
          </button>
          <div style={{ fontSize: 15, fontWeight: 600, marginTop: 16 }}>2. Ingresa este código</div>
          <div
            style={{ fontSize: 34, fontWeight: 700, letterSpacing: ".12em", margin: "8px 0", textAlign: "center", fontFamily: FUENTE_CODIGO, fontVariantNumeric: "slashed-zero", wordBreak: "break-all" }}
          >
            {c.codigo}
          </div>
          <button style={{ ...boton(C.verde), width: "100%" }} onClick={() => copiar(c.codigo)}>
            {copiado ? "Copiado" : "Copiar"}
          </button>
          <div style={{ fontSize: 13, color: C.ambar, marginTop: 8 }}>Pega el código con el botón Copiar, no lo escribas a mano.</div>
          {c.expiraAt && (
            <div style={{ fontSize: 13, color: C.suave, marginTop: 8 }}>
              El código vence a las {new Date(c.expiraAt).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}.
            </div>
          )}
        </div>
      )}

      {c.servicio === "claude_residente" && c.url && (
        <div style={{ marginTop: 10 }}>
          <button style={boton(C.azul, true)} onClick={() => window.open(c.url, "_blank", "noopener,noreferrer")}>
            Abrir sesión del residente
          </button>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        <button style={{ ...boton(), width: "100%" }} onClick={onCerrar}>Cerrar</button>
      </div>
    </div>
  );
};

const CuentasChips = ({ cuentas, copiado, conectadoOk, conectar, copiar, esMovil }) => {
  const [abierta, setAbierta] = useState(null); // servicio cuya hoja está abierta

  if (!cuentas) return <span style={{ fontSize: 13, color: C.suave }}>Cargando cuentas…</span>;
  const actual = abierta ? cuentas.find(c => c.servicio === abierta) : null;
  return (
    <>
      {cuentas.map(c => {
        const color = COLOR_ESTADO[c.estado] || C.suave;
        const esperando = c.estado === "esperando_aprobacion";
        return (
          <button
            key={c.servicio}
            className="chip-touch t-02"
            title={`${c.nombre}: ${TEXTO_ESTADO[c.estado] || c.estado}`}
            onClick={() => setAbierta(c.servicio)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 34, padding: "4px 10px", borderRadius: 8, border: `1px solid ${esperando ? C.ambar : C.borde}`, background: C.caja, color: C.texto, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }}
          >
            <span aria-hidden style={{ width: 8, height: 8, flex: "0 0 8px", borderRadius: 4, background: color }} />
            {NOMBRE_CORTO[c.servicio] || c.nombre}
          </button>
        );
      })}
      {actual && (
        <Hoja onCerrar={() => setAbierta(null)} maxWidth={520} centrada={!esMovil}>
          <DetalleCuenta c={actual} copiado={copiado} conectadoOk={conectadoOk} conectar={conectar} copiar={copiar} onCerrar={() => setAbierta(null)} />
        </Hoja>
      )}
    </>
  );
};

export default CuentasChips;
